import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import { CircularProgress, IconButton, Link, MenuItem, Select, Stack, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import type { WorkflowType } from "@entrilia/shared/api/workflowTypes";
import FileIcon from "@entrilia/shared/components/FileIcon";
import { getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import type { WorkflowGroup } from "../../../../api/types/workflowTypes";
import VisibleIcon from "@entrilia/shared/icons/VisibleIcon";
import type { UploadDocumentsMode } from "./UploadDocumentsDialog";
import type { FileEntry } from "./uploadDocumentsState";

interface Props {
  entry: FileEntry;
  mode: UploadDocumentsMode;
  onPreview: (id: string) => void;
  onCancel?: (id: string) => void;
  onRetry: (id: string) => void;
  onRemove: (id: string) => void;
  onSelectWorkflow?: (id: string, workflowType: WorkflowType) => void;
}

const isFileMatchingWorkflow = (entry: FileEntry, workflowType: WorkflowType): boolean =>
  entry.matchDecision?.workflowType === workflowType;

const StatusIndicator = ({ entry, mode }: { entry: FileEntry; mode: UploadDocumentsMode }) => {
  switch (entry.status) {
    case "Uploading":
    case "Queueing":
      return <CircularProgress color="secondary" size={16} />;
    case "Duplicate":
    case "UploadFailed":
    case "QueueFailed":
      return <ErrorRoundedIcon color="error" />;
    case "Uploaded":
      if (mode.kind === "match") {
        if (entry.analysisStatus === "Analyzing") {
          return <CircularProgress color="secondary" size={16} />;
        }
        if (entry.analysisStatus === "Failed") {
          return <ErrorRoundedIcon color="error" />;
        }
        return entry.selectedWorkflowType ? (
          <CheckCircleRoundedIcon color="success" />
        ) : (
          <HelpOutlineRoundedIcon color="warning" />
        );
      }
      if (entry.analysisStatus === "Analyzed" && !isFileMatchingWorkflow(entry, mode.workflowType)) {
        return <HelpOutlineRoundedIcon color="warning" />;
      }
      return <CheckCircleRoundedIcon color="success" />;
    case "Queued":
    case "WorkflowStarted":
      return <CheckCircleRoundedIcon color="success" />;
    default:
      return null;
  }
};

const StatusLabel = ({ entry, mode }: { entry: FileEntry; mode: UploadDocumentsMode }) => {
  if (entry.status === "Uploading") {
    return (
      <Typography variant="caption" noWrap color="textSecondary">
        Uploading…
      </Typography>
    );
  }
  if (entry.status === "UploadFailed") {
    return (
      <Tooltip title={entry.error ?? "Failed"}>
        <Typography variant="caption" color="error" noWrap>
          Upload failed
        </Typography>
      </Tooltip>
    );
  }
  if (entry.status === "Uploaded") {
    if (mode.kind === "match") {
      if (entry.analysisStatus === "Analyzing") {
        return (
          <Typography variant="caption" noWrap color="textSecondary">
            Analyzing…
          </Typography>
        );
      }
      if (entry.analysisStatus === "Failed") {
        return (
          <Tooltip title={entry.analysisError ?? "Failed"}>
            <Typography variant="caption" color="error" noWrap>
              Analysis failed
            </Typography>
          </Tooltip>
        );
      }
      return null;
    }
    return (
      <Typography variant="caption" noWrap sx={{ color: "success.main" }}>
        Ready
      </Typography>
    );
  }
  if (entry.status === "Duplicate") {
    return (
      <Typography variant="caption" color="error" noWrap>
        Duplicate
      </Typography>
    );
  }
  if (entry.status === "Queueing") {
    return (
      <Typography variant="caption" noWrap color="textSecondary">
        Queueing…
      </Typography>
    );
  }
  if (entry.status === "QueueFailed") {
    return (
      <Tooltip title={entry.runError ?? "Failed to queue workflow."}>
        <Typography variant="caption" color="error" noWrap>
          Queue failed
        </Typography>
      </Tooltip>
    );
  }
  if (entry.status === "Queued") {
    return (
      <Typography variant="caption" noWrap color="textSecondary">
        Starting workflow…
      </Typography>
    );
  }
  if (entry.status === "WorkflowStarted") {
    return entry.workflowDestination ? (
      <Link component={RouterLink} to={entry.workflowDestination} target="_blank" rel="noopener noreferrer">
        <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: "center" }}>
          Started
          <OpenInNewRoundedIcon sx={{ fontSize: "inherit" }} />
        </Stack>
      </Link>
    ) : (
      <Typography variant="caption" noWrap sx={{ color: "success.main" }}>
        Started
      </Typography>
    );
  }
  return null;
};

const AnalysisLabel = ({ entry, mode }: { entry: FileEntry; mode: UploadDocumentsMode }) => {
  if (mode.kind !== "run") {
    return null;
  }

  if (entry.analysisStatus === "Analyzing") {
    return (
      <Typography variant="caption" noWrap color="textSecondary">
        Analyzing…
      </Typography>
    );
  }
  if (entry.analysisStatus === "Analyzed" && isFileMatchingWorkflow(entry, mode.workflowType)) {
    return (
      <>
        <Typography variant="caption" noWrap sx={{ color: "success.main" }}>
          Matched
        </Typography>
        <Tooltip title={getAnalysisTooltip(entry) ?? ""}>
          <InfoOutlinedIcon color="action" />
        </Tooltip>
      </>
    );
  }
  if (entry.analysisStatus === "Analyzed" && !isFileMatchingWorkflow(entry, mode.workflowType)) {
    return (
      <Tooltip title={getAnalysisTooltip(entry) ?? "No matching workflow type was identified for this document."}>
        <Typography variant="caption" color="error" noWrap>
          Wrong file
        </Typography>
      </Tooltip>
    );
  }
  if (entry.analysisStatus === "Failed") {
    return (
      <Tooltip title={entry.analysisError ?? "Analysis failed"}>
        <Typography variant="caption" color="error" noWrap>
          Analysis failed
        </Typography>
      </Tooltip>
    );
  }
  return null;
};

const getAnalysisTooltip = (entry: FileEntry): string | undefined => {
  if (entry.matchDecision) {
    const pct = Math.round(entry.matchDecision.confidence * 100);
    return `${pct}% confidence \u2014 ${entry.matchDecision.reason}`;
  }
  return undefined;
};

const getMatchInfoTooltip = (entry: FileEntry): string | undefined => {
  if (entry.status !== "Uploaded" && entry.status !== "QueueFailed") {
    return undefined;
  }

  if (entry.matchDecision) {
    const pct = Math.round(entry.matchDecision.confidence * 100);
    return `${pct}% confidence \u2014 ${entry.matchDecision.reason}`;
  }

  if (entry.analysisStatus === "Analyzed" && !entry.matchDecision) {
    return "No matching workflow type was identified for this document. Please select a workflow manually.";
  }

  return undefined;
};

const canShowWorkflowSelector = (entry: FileEntry): boolean =>
  (entry.status === "Uploaded" && entry.analysisStatus !== "Analyzing" && entry.analysisStatus !== "Failed") ||
  entry.status === "QueueFailed";

const UploadDocumentsFileRow = ({ entry, mode, onPreview, onCancel, onRetry, onRemove, onSelectWorkflow }: Props) => {
  const matchInfoTooltip = mode.kind === "match" ? getMatchInfoTooltip(entry) : undefined;

  const canRetry =
    entry.status === "UploadFailed" ||
    (mode.kind === "run" && entry.status === "QueueFailed") ||
    (mode.kind === "match" && entry.status === "Uploaded" && entry.analysisStatus === "Failed");

  const canCancel =
    mode.kind === "match" &&
    onCancel &&
    (entry.status === "Uploading" ||
      (entry.status === "Uploaded" && (entry.analysisStatus === "Pending" || entry.analysisStatus === "Analyzing")));

  const canDelete =
    entry.status !== "Uploading" &&
    entry.status !== "Queueing" &&
    entry.status !== "Queued" &&
    entry.status !== "WorkflowStarted" &&
    !(mode.kind === "match" && entry.status === "Uploaded" && entry.analysisStatus === "Analyzing");

  const showSelector = mode.kind === "match" && onSelectWorkflow && canShowWorkflowSelector(entry);
  const workflowGroups = mode.kind === "match" ? mode.workflowGroups : [];

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={[{ alignItems: "center", py: 0.25 }, entry.status === "Duplicate" && { opacity: 0.6 }]}
    >
      <StatusIndicator entry={entry} mode={mode} />
      <FileIcon fileExtension={getFileExtension(entry.file.name)} />
      <Typography
        noWrap
        sx={{ flex: "1 1 0", minWidth: 0, textDecoration: entry.status === "Duplicate" ? "line-through" : undefined }}
      >
        {entry.file.name}
      </Typography>
      <Tooltip title="Preview file">
        <IconButton onClick={() => onPreview(entry.id)}>
          <VisibleIcon />
        </IconButton>
      </Tooltip>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexShrink: 0 }}>
        <StatusLabel entry={entry} mode={mode} />
        <AnalysisLabel entry={entry} mode={mode} />

        {canCancel && (
          <Tooltip title="Cancel and choose manually">
            <IconButton onClick={() => onCancel!(entry.id)}>
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        )}

        {showSelector && (
          <Select
            value={entry.selectedWorkflowType ?? ""}
            displayEmpty
            onChange={(e) => onSelectWorkflow!(entry.id, e.target.value as WorkflowType)}
            sx={{ minWidth: 280 }}
          >
            <MenuItem value="" disabled>
              Select workflow
            </MenuItem>
            {workflowGroups.map((g: WorkflowGroup) => (
              <MenuItem key={g.templateId} value={g.workflowType}>
                {g.name}
              </MenuItem>
            ))}
          </Select>
        )}

        {matchInfoTooltip && (
          <Tooltip title={matchInfoTooltip}>
            <InfoOutlinedIcon color="action" />
          </Tooltip>
        )}

        {canRetry && (
          <Tooltip title="Retry">
            <IconButton onClick={() => onRetry(entry.id)}>
              <ReplayRoundedIcon />
            </IconButton>
          </Tooltip>
        )}

        {canDelete && (
          <Tooltip title="Remove">
            <IconButton color="error" onClick={() => onRemove(entry.id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};

export default UploadDocumentsFileRow;
